import { call, put, select, takeEvery } from 'typed-redux-saga/macro';

import { HYDRATE_WATERMARK_IMAGES, UPDATE_WATERMARK } from '../actions';
import { selectAppliedWatermarks } from '../selectors';
import { deriveWatermarkImage } from '../watermarkImage';

/**
 * Re-derive an image watermark's DRAWN form when the operator changes what it
 * is derived FROM (VE-24693).
 *
 * The asset stores only the color base plus the black-&-white and color
 * choices, because the drawn PNG is a pure function of the three. `openDocument`
 * rebuilds it once, on open. That was the whole of the derivation — so between
 * open and reopen the derived form was frozen: flipping "Grayscale" on an
 * applied logo updated the flag, marked the document dirty, and changed
 * nothing. The canvas kept the color logo, and `exportPush` reads the same
 * `imageDataUrl`, so the burned-in PDF kept it too. The choice only appeared
 * after closing and reopening the document, which is not a place anyone would
 * think to look.
 *
 * So derivation belongs wherever those inputs change, not only on open. This
 * watches the one action that can change them.
 *
 * It dispatches that hydrate with `origin: 'edit'`, which is what keeps the new
 * raster on the PREVIEW side while a watermark panel is open: the flag change
 * it answers has not been accepted yet, so its artwork must not reach the
 * committed mark, where a Cancel could not take it back and the export would
 * burn it. (`openDocument` uses the same action with `origin: 'restore'` for
 * the opposite reason — that raster IS the document's.)
 *
 * Piggy-backs on HYDRATE_WATERMARK_IMAGES rather than another UPDATE_WATERMARK:
 * the user's own UPDATE has already pushed the undo snapshot and marked the
 * document dirty, and a second dirtying action for the machine's half of the
 * same edit would burn an extra undo slot for a step the user cannot perceive
 * as separate.
 */
function* onWatermarkUpdated(
  action: ReturnType<typeof UPDATE_WATERMARK>
): Generator<unknown, void, unknown> {
  const { instanceId, patch } = action.payload;

  // Only the two fields the drawn form is derived from. `scale`, `rotation`,
  // `opacity` and position are applied at draw time and need no re-render.
  if (patch.grayscale === undefined && patch.color === undefined) {
    return;
  }

  const before = (yield* select(selectAppliedWatermarks)).find(
    (w) => w.instanceId === instanceId
  );
  if (!before || before.type !== 'image' || !before.imageBaseDataUrl) {
    return;
  }

  const { grayscale, color, imageBaseDataUrl } = before;
  let drawn: string;
  try {
    drawn = yield* call(deriveWatermarkImage, imageBaseDataUrl, {
      grayscale,
      color,
    });
  } catch (err) {
    // Same posture as the open-time hydrate: the mark keeps whatever it was
    // last drawn as rather than blanking out.
    console.warn('Watermark image could not be re-derived', err);
    return;
  }

  // The derive is a canvas round-trip, so the operator can toggle again while
  // it runs and two of these can be in flight for one watermark. Applying a
  // result whose inputs are already stale would leave the canvas showing a
  // choice that is no longer selected, so drop it and let the later run land.
  const after = (yield* select(selectAppliedWatermarks)).find(
    (w) => w.instanceId === instanceId
  );
  if (
    !after ||
    after.grayscale !== grayscale ||
    after.color !== color ||
    after.imageBaseDataUrl !== imageBaseDataUrl
  ) {
    return;
  }

  yield* put(
    HYDRATE_WATERMARK_IMAGES({
      images: [{ instanceId, imageDataUrl: drawn }],
      origin: 'edit',
    })
  );
}

export default function* watermarkDeriveSaga() {
  yield* takeEvery(UPDATE_WATERMARK, onWatermarkUpdated);
}
